/**
 * Sidecar path resolver — locations under `~/.skaile/sidecars/` (PR-3 § D.1).
 *
 * `~/.skaile/sidecars/` is a single git repo containing one subdirectory per
 * registered sidecar. Each subdirectory carries the local-overlay manifest
 * (`skaile.manifest.yaml`), a provenance README, and a lock file.
 *
 * Path resolution honors the `SKAILE_HOME` env var (defaults to `~/.skaile`)
 * mirroring the style used by `SKAILE_LIBRARIES_DIR` (which already overrides
 * the library checkouts dir). Setting `SKAILE_HOME` is the recommended hook
 * for test isolation: a single env var moves the sidecar root, the libraries
 * dir (when not separately overridden), and the catalog cache together.
 *
 * @docLink packages/library/concepts#sidecar-paths
 */

import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";

import { getGlobalCacheDir, mergeLegacyEntriesInto } from "@skaile/workspaces/core";
import type { SourceConfig } from "@skaile/workspaces/discovery";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Resolved sidecar paths for a given slug. All paths are absolute.
 *
 * @docLink packages/library/concepts#sidecar-paths
 */
export interface SidecarPaths {
  /** Root sidecar directory, `~/.skaile/sidecars` (or `$SKAILE_HOME/sidecars`). */
  rootDir: string;
  /** Per-source sidecar dir: `<rootDir>/<slug>`. */
  sidecarDir: string;
  /** Manifest file: `<sidecarDir>/skaile.manifest.yaml`. */
  manifestFile: string;
  /** Provenance README: `<sidecarDir>/README.md`. */
  readmeFile: string;
  /** Lock file: `<sidecarDir>/skaile.manifest.lock.json`. */
  lockFile: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SKAILE_HOME_ENV = "SKAILE_HOME";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** True if `dir` is itself a git repo (has its own `.git`) — i.e. a source clone. */
function isClone(dir: string): boolean {
  return fs.existsSync(path.join(dir, ".git"));
}

/**
 * Sidecar root: `$SKAILE_HOME/sidecars` (default `~/.skaile/sidecars`). Renamed
 * from the old `~/.skaile/sources` (which collided with the clone cache). On first
 * call it migrates the legacy `~/.skaile/sources` namespace by splitting it: source
 * clones (own `.git`) are claimed by the clone cache (`getGlobalCacheDir`), and the
 * remainder — the sidecar git repo (root `.git`, `README.md`, `.gitattributes`,
 * slug subdirs) — moves here. Authored content: a failed move leaves the legacy
 * entry in place rather than masking it.
 *
 * Does **not** create the directory — callers (e.g. `SidecarGit.ensureInitialized`)
 * are responsible for materialising it.
 *
 * @returns Absolute path to `~/.skaile/sidecars` (or env override)
 * @docLink packages/library/concepts#sidecar-paths
 */
export function getSidecarRoot(): string {
  const home = process.env[SKAILE_HOME_ENV] ?? path.join(os.homedir(), ".skaile");
  const sidecars = path.join(home, "sidecars");
  const legacy = path.join(home, "sources");
  // Order matters: clones MUST be claimed by the cache first so the second
  // pass's `!isClone` filter only sees the sidecar remainder. This resolver
  // therefore also migrates clones as a side effect — keep the two calls together.
  mergeLegacyEntriesInto(getGlobalCacheDir(), legacy, isClone);
  mergeLegacyEntriesInto(sidecars, legacy, (p) => !isClone(p));
  return sidecars;
}

/**
 * Resolve all sidecar paths for a given slug.
 *
 * @param slug - Sidecar slug (subdirectory name under `~/.skaile/sidecars/`)
 * @returns Bundle of all relevant absolute paths
 * @docLink packages/library/concepts#sidecar-paths
 */
export function resolveSidecarPaths(slug: string): SidecarPaths {
  const rootDir = getSidecarRoot();
  const sidecarDir = path.join(rootDir, slug);
  return {
    rootDir,
    sidecarDir,
    manifestFile: path.join(sidecarDir, "skaile.manifest.yaml"),
    readmeFile: path.join(sidecarDir, "README.md"),
    lockFile: path.join(sidecarDir, "skaile.manifest.lock.json"),
  };
}

/**
 * Derive a sidecar slug from a source config + source path, applying
 * collision resolution against the set of slugs already in use.
 *
 * Derivation order (per PR-3 § D.1):
 *   1. `name:` field of the upstream source config (if non-empty)
 *   2. `path.basename(sourcePath)` (fallback)
 *
 * Collision suffix: `-2`, `-3`, ... appended until unique against
 * `existingSlugs`.
 *
 * Empty/dot/segmented basenames fall through to `unnamed-source`.
 *
 * @param sourceConfig - Validated source config (may be `undefined`)
 * @param sourcePath - Filesystem path of the source repo
 * @param existingSlugs - Slugs already in use under the sidecar root
 * @returns A unique, slugified directory name
 * @docLink packages/library/concepts#derive-slug
 */
export function deriveSlug(
  sourceConfig: SourceConfig | undefined | null,
  sourcePath: string,
  existingSlugs: Iterable<string>,
): string {
  // Step 1: pick the candidate from name: -> basename -> fallback.
  const fromConfig =
    sourceConfig && typeof (sourceConfig as { name?: unknown }).name === "string"
      ? ((sourceConfig as { name?: string }).name as string)
      : undefined;
  const base =
    fromConfig && fromConfig.length > 0
      ? slugify(fromConfig)
      : slugify(path.basename(sourcePath || "")) || "unnamed-source";

  // Step 2: collision suffix.
  const taken = new Set<string>(existingSlugs);
  if (!taken.has(base)) return base;
  for (let i = 2; i < 1_000; i++) {
    const candidate = `${base}-${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  // Pathological fallback (1k+ collisions): random suffix.
  return `${base}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * List the slugs currently present on disk under {@link getSidecarRoot}.
 *
 * Returns an empty array when the root directory doesn't exist yet (first
 * sidecar). Non-directory entries (e.g. the root `README.md`,
 * `.gitattributes`) are filtered out.
 *
 * @returns Lexically sorted list of slug directory names
 * @docLink packages/library/concepts#sidecar-paths
 */
export function listSidecarSlugsOnDisk(): string[] {
  const root = getSidecarRoot();
  if (!fs.existsSync(root)) return [];
  const out: string[] = [];
  for (const entry of fs.readdirSync(root, { withFileTypes: true })) {
    if (!entry.isDirectory()) continue;
    if (entry.name.startsWith(".")) continue; // skip .git / .gitattributes anomalies
    out.push(entry.name);
  }
  return out.sort();
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Conservative slugifier: lowercase, replace whitespace + non-(alnum/dash/dot)
 * with `-`, collapse repeated dashes, strip leading/trailing dashes.
 */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^[-.]+|[-.]+$/g, "");
}
